import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from './shared';
import type { RespondentType } from './shared';
import './AdminPages.css';

const EXPORT_OPTIONS_BY_ROLE: Record<RespondentType, { value: string; label: string }[]> = {
  student: [
    { value: '', label: 'All Surveys' },
    { value: 'k-2', label: 'K-2 Survey' },
    { value: '3-5', label: 'Grades 3-5 Survey' },
    { value: '6-8', label: 'Grades 6-8 Survey' },
    { value: 'high-school', label: 'High School Survey' },
    { value: 'virtual-k8', label: 'Virtual K-8 Survey' },
    { value: 'virtual-hs', label: 'Virtual High School Survey' },
  ],
  staff: [
    { value: '', label: 'All Surveys' },
    { value: 'staff-climate', label: 'Staff Climate Survey' },
  ],
  parent: [
    { value: '', label: 'All Surveys' },
  ],
};

const DataExport: React.FC = () => {
  const { token } = useAuth();
  const { respondentType } = useAdminContext();
  const [surveyId, setSurveyId] = useState<string>('');
  const [format, setFormat] = useState<'json' | 'csv'>('csv');
  const [isExporting, setIsExporting] = useState(false);

  const surveyOptions = EXPORT_OPTIONS_BY_ROLE[respondentType] || EXPORT_OPTIONS_BY_ROLE.student;

  const handleExport = async () => {
    if (!token) return;

    setIsExporting(true);

    try {
      const params = new URLSearchParams();
      params.set('format', format);
      params.set('role', respondentType);
      if (surveyId) {
        params.set('surveyType', surveyId);
      }

      const response = await fetch(`/api/admin/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      if (format === 'csv') {
        // Download CSV
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // Download JSON
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Export Data</h2>
        <p className="page-description">Download aggregated survey response data</p>
      </div>

      <div className="export-form">
        <div className="form-group">
          <label htmlFor="surveyId">Survey (optional)</label>
          <select
            id="surveyId"
            value={surveyId}
            onChange={(e) => setSurveyId(e.target.value)}
          >
            {surveyOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Export Format</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="format"
                value="csv"
                checked={format === 'csv'}
                onChange={() => setFormat('csv')}
              />
              <span>CSV</span>
              <span className="radio-desc">Spreadsheet-compatible format</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="format"
                value="json"
                checked={format === 'json'}
                onChange={() => setFormat('json')}
              />
              <span>JSON</span>
              <span className="radio-desc">Structured data format</span>
            </label>
          </div>
        </div>

        <div className="export-note">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 7V11M8 5V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <p>
            Export includes aggregated response counts only. Individual responses are not included to protect respondent privacy.
          </p>
        </div>

        <button
          className="export-button"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? (
            <>
              <div className="button-spinner" />
              Exporting...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17 12.5V15.8333C17 16.2754 16.8244 16.6993 16.5118 17.0118C16.1993 17.3244 15.7754 17.5 15.3333 17.5H4.66667C4.22464 17.5 3.80072 17.3244 3.48816 17.0118C3.17559 16.6993 3 16.2754 3 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export Data
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DataExport;
